import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, Check, Sparkles } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { track } from '../lib/posthog'
import { useFoundingStatus } from '../hooks/useFoundingStatus'

// ─── "What you get" content ───────────────────────────────────────────────────

const WHAT_YOU_GET: { title: string; body: string }[] = [
  { title: 'Your whole household, one subscription', body: 'Up to 6 members. Everyone gets full access.' },
  { title: 'Unlimited receipt and recipe scanning',  body: 'No monthly limits on imports or AI features.' },
  { title: 'AI meal suggestions from your pantry',   body: 'Personalized recipes using what you already have.' },
  { title: 'Auto shopping list emails',              body: 'Your weekly list, delivered automatically.' },
  { title: 'Alexa voice integration',                body: 'Add to your list and check your pantry hands-free.' },
]

type Tier = 'founding' | 'annual' | 'monthly'

// ─── Paywall ──────────────────────────────────────────────────────────────────

export default function Paywall() {
  const navigate = useNavigate()
  const founding = useFoundingStatus()

  const [loadingTier, setLoadingTier] = useState<Tier | null>(null)
  const [restoring,   setRestoring]   = useState(false)
  const [toast,       setToast]       = useState<string | null>(null)

  // The trigger source — which feature hit a limit and pushed the user to
  // the paywall — is set by the caller via navigate('/paywall', { state }).
  // Falling back to 'direct' for users who navigate here from Settings.
  useEffect(() => {
    const trigger = (window.history.state?.usr?.trigger as string | undefined) ?? 'direct'
    track('paywall_viewed', { trigger })
  }, [])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  // ── Subscribe ─────────────────────────────────────────────────────────────

  async function handleSubscribe(tier: Tier) {
    setLoadingTier(tier)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { showToast('Please sign in first'); return }

      // Founding tier always uses the annual price ID — backend re-validates
      // by checking price.recurring.interval === 'year', so a tampered
      // monthly price ID would be rejected with 400.
      const priceId = tier === 'monthly'
        ? import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID
        : import.meta.env.VITE_STRIPE_ANNUAL_PRICE_ID

      track('subscription_started', { plan: tier, source: 'web' })

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            priceId,
            userId:    session.user.id,
            userEmail: session.user.email,
            ...(tier === 'founding' ? { founding: true } : {}),
          }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        console.error('[Paywall] checkout failed:', data)
        showToast('Checkout failed. Try again.')
        return
      }

      if (data.url) window.location.href = data.url
    } catch (err: unknown) {
      console.error('[Paywall] unexpected error:', err)
      showToast(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoadingTier(null)
    }
  }

  // ── Restore ───────────────────────────────────────────────────────────────

  async function handleRestore() {
    setRestoring(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { showToast('Please sign in first'); return }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/restore-purchase`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ userId: session.user.id }),
        }
      )
      const data = await response.json()
      if (data.restored) {
        showToast('Purchase restored.')
        setTimeout(() => navigate('/home'), 1500)
      } else {
        showToast('No active subscription found.')
      }
    } catch {
      showToast('Contact privacy@pantryioapp.com to restore')
    } finally {
      setRestoring(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Show the founding card during loading (skeleton) and when available;
  // hide entirely on error or sold-out (founding.available stays false in
  // both cases — fail closed).
  const showFoundingCard = founding.isLoading || founding.available

  return (
    <main className="min-h-screen bg-cream pb-16">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Back header */}
        <div className="pt-3 pb-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <p className="flex-1 font-serif italic text-[13px] text-ink-muted">
            Pantry+
          </p>
        </div>

        {/* Masthead */}
        <div className="pt-8 pb-2 text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-2">
            Pantry+
          </p>
          <h1 className="font-serif text-[28px] text-ink leading-[1.1] tracking-[-0.015em] max-w-[340px] mx-auto">
            One Pantry+ for your <em className="italic">whole</em> household
          </h1>
          <p className="font-serif italic text-[14px] text-ink-muted mt-3 leading-[1.5] max-w-[340px] mx-auto">
            One subscription covers everyone you live with — up to six members. No seats, no per-person pricing.
          </p>
        </div>

        {/* Trial disclosure — Google Play / Apple "clear and conspicuous"
            pricing rule. The copy IS the compliance, don't water it down. */}
        <div className="bg-cream-warm border border-border-sand rounded-2xl px-4 py-3 mt-7 mb-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta">
            Free for 7 days
          </p>
          <p className="font-serif text-[13px] italic text-ink leading-[1.45] mt-1">
            Try Pantry+ free for a week. We'll remind you 3 days before your trial ends. Cancel anytime — no charge during the trial.
          </p>
        </div>

        {/* ── Founding card (conditional) ──────────────────────────────────── */}
        {showFoundingCard && (
          <div className="bg-cream-warm border-2 border-terracotta/40 rounded-2xl p-5 mb-3">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-terracotta" strokeWidth={1.7} />
              <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-terracotta">
                Founding Member · Limited
              </p>
            </div>

            <p className="font-serif text-[14px] italic text-ink-muted line-through tabular-nums">
              $39.99/yr
            </p>
            <p className="font-serif text-[28px] font-medium text-ink leading-none tracking-[-0.01em] tabular-nums mt-1">
              $24.99/yr forever
            </p>
            <p className="font-serif italic text-[12px] text-ink-muted mt-1">
              Locked at this price for life
            </p>
            <p className="text-[11px] italic font-serif text-ink-muted mt-1">
              Free for 7 days, then $24.99/yr forever
            </p>
            <p className="text-[11px] uppercase tracking-[0.1em] font-medium text-forest mt-2">
              Covers up to 6 household members
            </p>

            {/* Counter — skeleton during load (no layout shift), urgent
                terracotta when fewer than 50 spots remain. */}
            {founding.isLoading ? (
              <div className="h-4 w-32 bg-paper/60 rounded animate-pulse mt-3" />
            ) : (
              <p className={`text-[12px] tabular-nums mt-3 ${founding.remaining < 50 ? 'text-terracotta' : 'text-ink-muted'}`}>
                {founding.remaining} of {founding.total} spots left
              </p>
            )}

            <button
              type="button"
              onClick={() => handleSubscribe('founding')}
              disabled={loadingTier !== null || founding.isLoading}
              className="w-full mt-4 rounded-2xl bg-forest text-forest-ink py-3 font-bold text-[14px] tracking-[-0.01em] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loadingTier === 'founding' && (
                <span className="h-4 w-4 rounded-full border-2 border-forest-ink border-t-transparent animate-spin" />
              )}
              {loadingTier === 'founding' ? 'Loading…' : 'Claim founding price'}
            </button>
          </div>
        )}

        {/* ── Annual card ───────────────────────────────────────────────────── */}
        <div className="bg-paper border border-border-sand rounded-2xl p-4 mb-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-ink-muted font-medium">
            Annual · Save 33%
          </p>
          <p className="font-serif text-[24px] text-ink mt-1 leading-none tracking-[-0.01em] tabular-nums">
            $39.99/yr
          </p>
          <p className="font-serif italic text-[11px] text-ink-muted mt-1.5">
            $3.33/mo · saves you $20
          </p>
          <p className="text-[11px] italic font-serif text-ink-muted mt-1">
            Free for 7 days, then $39.99/yr
          </p>
          <p className="text-[11px] uppercase tracking-[0.1em] font-medium text-forest mt-2">
            Covers up to 6 household members
          </p>
          <button
            type="button"
            onClick={() => handleSubscribe('annual')}
            disabled={loadingTier !== null}
            className="w-full mt-3 rounded-2xl bg-forest text-forest-ink py-3 font-bold text-[14px] tracking-[-0.01em] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loadingTier === 'annual' && (
              <span className="h-4 w-4 rounded-full border-2 border-forest-ink border-t-transparent animate-spin" />
            )}
            {loadingTier === 'annual' ? 'Loading…' : 'Subscribe'}
          </button>
        </div>

        {/* ── Monthly card — smallest visual weight ─────────────────────────── */}
        <div className="bg-paper border border-border-sand rounded-2xl px-4 py-3 mb-5">
          <p className="font-serif text-[20px] text-ink leading-none tracking-[-0.01em] tabular-nums">
            $4.99/mo
          </p>
          <p className="text-[11px] italic font-serif text-ink-muted mt-1">
            Free for 7 days, then $4.99/mo
          </p>
          <p className="text-[11px] uppercase tracking-[0.1em] font-medium text-forest mt-2">
            Covers up to 6 household members
          </p>
          <button
            type="button"
            onClick={() => handleSubscribe('monthly')}
            disabled={loadingTier !== null}
            className="w-full mt-3 rounded-2xl border border-terracotta text-terracotta py-3 font-medium text-[14px] tracking-[-0.01em] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loadingTier === 'monthly' && (
              <span className="h-4 w-4 rounded-full border-2 border-terracotta border-t-transparent animate-spin" />
            )}
            {loadingTier === 'monthly' ? 'Loading…' : 'Subscribe'}
          </button>
        </div>

        {/* ── What you get ──────────────────────────────────────────────────── */}
        <div className="bg-paper border border-border-sand rounded-2xl px-5 mb-5">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted pt-4 pb-1">
            What you get
          </p>
          <div className="divide-y divide-border-hairline">
            {WHAT_YOU_GET.map(item => (
              <div key={item.title} className="flex items-start gap-3 py-3">
                <Check className="h-4 w-4 text-forest mt-0.5 flex-shrink-0" strokeWidth={1.7} />
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                    {item.title}
                  </p>
                  <p className="font-serif italic text-[12px] text-ink-muted mt-0.5 leading-[1.4]">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="h-2" />
        </div>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <div className="text-center mt-5">
          <button
            type="button"
            onClick={handleRestore}
            disabled={restoring}
            className="font-serif italic text-[12px] text-ink-muted underline-offset-2 underline decoration-ink-muted/30 disabled:opacity-50"
          >
            {restoring ? 'Restoring…' : 'Restore purchase'}
          </button>
          <p className="font-serif text-[11px] text-ink-muted/80 mt-2 leading-[1.5]">
            Cancel anytime · No auto-renew tricks · <a href="/refund-policy" className="underline underline-offset-2 decoration-ink-muted/40">Refund Policy</a>
          </p>
        </div>

      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="fixed top-5 left-1/2 -translate-x-1/2 z-50 rounded-full bg-ink text-cream px-5 py-2.5 font-serif text-[13px] shadow-xl whitespace-nowrap max-w-[90vw] text-center"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

    </main>
  )
}
